import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { Country, CreateCountryParams, UpdateCountryParams } from '@INTERFACES/api/countries';

// Read API (uses API_PREFIX for read operations)
const countriesReadApi = createApi({
    reducerPath: 'countries-read-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Country'],
    endpoints: (builder) => ({
        getAll: builder.query<Country[], void>({
            query: () => '/countries/',
            providesTags: ['Country'],
        }),
        getById: builder.query<Country, number>({
            query: (id) => `/countries/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Country', id }],
        }),
    }),
});

// Write API (uses ADMIN_API_PREFIX for write operations)
const countriesWriteApi = createApi({
    reducerPath: 'countries-write-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Country'],
    endpoints: (builder) => ({
        create: builder.mutation<Country, CreateCountryParams>({
            query: (params) => ({
                url: '/countries/',
                method: 'POST',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: ['Country'],
        }),
        update: builder.mutation<Country, UpdateCountryParams>({
            query: ({ id, ...params }) => ({
                url: `/countries/${id}/`,
                method: 'PUT',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Country', id }],
        }),
        partialUpdate: builder.mutation<Country, UpdateCountryParams>({
            query: ({ id, ...params }) => ({
                url: `/countries/${id}/`,
                method: 'PATCH',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Country', id }],
        }),
        delete: builder.mutation<void, number>({
            query: (id) => ({
                url: `/countries/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Country'],
        }),
    }),
});

// Export both APIs for middleware and reducer registration
export const countriesAdminApi = countriesWriteApi; // Main API for backwards compatibility
export { countriesReadApi };

// Export hooks from both APIs
export const {
    useGetAllQuery: useGetAllCountries,
    
} = countriesReadApi;

export const {
    useCreateMutation: useCreateCountry,
    useUpdateMutation: useUpdateCountry,
    
    useDeleteMutation: useDeleteCountry,
} = countriesWriteApi;
